const initSearchLocationInput = ({ input, onChange }) => {
  onGoogleLoad(() => {
    const searchBox = new google.maps.places.SearchBox(input)
    searchBox.addListener("places_changed", () => {
      const places = searchBox.getPlaces()
      const place = places[0]
      if (!place) { return }
      onChange({
        name: input.value,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      })
    })
  })
}

class ProductsSearchForm {
  constructor($container) {
    this.$container = $container

    this.pikadayInstances = {};
    this.showDateToPicker = false;

    this.debouncedReloadResults = _.debounce((...args) => this.reloadResults(...args), 500)
    // Reload results when filters get changed, 
    // but with a debounced delay 
    this.$container.on("change", "input, select", event => {
      // Ignore changes in location name input 
      // (they are handled separately by a different callback)
      if (event.target && event.target.name === "lq") {
        return
      }
      // Ignore changes in date inputs
      // (they are handled separately by a different callback)
      if (event.target.className && event.target.className.indexOf("js-dateInput") !== -1) {
        return
      }
      this.debouncedReloadResults({ o: 0 })
    })

    // Reload results immediatily on submit click
    this.$container.on("submit", "form", event => {
      event.preventDefault()
      this.reloadResults({
        o: 0
      })
      return false
    })

    // Previous page click
    this.$container.on("click", ".js-ProductsSearch-prevpage", event => {
      event.preventDefault()
      this.reloadResults({
        o: Math.max(0, this.offset - this.limit),
      }).then(() => {
        window.scrollTo(0, 0)
      })
    })

    // Next page click
    this.$container.on("click", ".js-ProductsSearch-nextpage", event => {
      event.preventDefault()
      this.reloadResults({
        o: this.offset + this.limit
      }).then(() => {
        window.scrollTo(0, 0)
      })
    })

    this.$container.on("click", ".filter-clear-button", event => {
      event.preventDefault()
      let params = {}
      switch (event.target.dataset.name) {
        case "lq":
          params = { lq: null, lat: null, lng: null, radius: null }
          break
        case "dates":
          params = { date_from: null, date_to: null }
          break
        default:
          params = { [event.target.dataset.name]: null }
      }
      this.reloadResults({
        o: 0,
        ...params,
      })
    })

    window.onpopstate = event => {
      if (event.state && event.state.productsSearchFormData) {
        this.reloadResults(event.state.productsSearchFormData, { pushHistoryState: false })
      }
    }
    window.history.replaceState({ productsSearchFormData: this.getFormData() }, document.title, window.location.href)

    this.renderForm(this.$container.find("form")[0])
  }

  get offset() {
    return +this.$container.find("input[name=o]").val()
  }

  get limit() {
    return +this.$container.find("input[name=l]").val()
  }
  
  getFormData() {
    const data = {
      q: this.$container.find("input[name=q]").val(),
      lq: this.$container.find("input[name=lq]").val(),
      lat: this.$container.find("input[name=lat]").val(),
      lng: this.$container.find("input[name=lng]").val(),
      radius: this.$container.find("select[name=radius]").val(),
      date_from: this.$container.find("input[name=date_from]").val(),
      date_to: this.$container.find("input[name=date_to]").val(),
      category_ids: this.$container.find("input[name=\"category_ids[]\"]:checked").map((i, el) => el.value).get(),
      location_ids: this.$container.find("input[name=\"location_ids[]\"]:checked").map((i, el) => el.value).get(),
      locales: this.$container.find("input[name=\"locales[]\"]:checked").map((i, el) => el.value).get(),
      o: this.offset,
      // l: this.limit,
    }
    if (!data.lat && !data.lng) {
      delete data.lq
      delete data.lat
      delete data.lng
      delete data.radius
    }
    return data
  }
  
  toggleLoadingDimmer(visible) {
    this.$container.find(".ProductsSearch-dimmer").prop("hidden", !visible)
  }

  reloadResults(newFormData = {}, { pushHistoryState = true } = {}) {
    const url = this.$container.find("form").attr("action")
    const formData = _.pickBy({ ...this.getFormData(), ...newFormData }, v => !!v)

    this.toggleLoadingDimmer(true)

    if (pushHistoryState) {
      const newWindowUrl = location.origin + `/s?` + $.param(formData)
      window.history.pushState({ productsSearchFormData: formData }, `${I18n.t("frontend.products.search.site_title.default")} | ${I18n.t("fe.common.site_title")}`, newWindowUrl)
    }

    return new Promise((resolve, reject) => {
      const _requestPromise = this._requestPromise = $.get(url, { ...formData, ajax: 1 }).then(
        response => {
          if (this._requestPromise !== _requestPromise) { return }
          this.toggleLoadingDimmer(false)
          this.$container.find("form")[0].outerHTML = $(response).find("form")[0].outerHTML
          this.renderForm(this.$container.find("form")[0])
          resolve()
        },
        error => {
          if (this._requestPromise !== _requestPromise) { return }
          this.toggleLoadingDimmer(false)
          console.error && console.error(error)
          reject()
        }
      )
    })
  }

  renderForm(form) {
    const locationNameInput = form.querySelector("input[name=lq]")
    const locationLatInput = form.querySelector("input[name=lat]")
    const locationLngInput = form.querySelector("input[name=lng]")
    
    initSearchLocationInput({
      input: locationNameInput,
      onChange: ({ name, lat, lng }) => {
        locationNameInput.value = name
        locationLatInput.value = lat
        locationLngInput.value = lng
        this.debouncedReloadResults({ o: 0 })
      }
    })

    this.initDatePickers(form)
  }

  initDatePickers(form) {
    const dateFromInput = form.querySelectorAll(".js-dateInput[name='date_from']")[0],
          dateToInput = form.querySelectorAll(".js-dateInput[name='date_to']")[0];

    const dateFromPicker = new Pikaday({
      field: dateFromInput,
      minDate: new Date(),
      onSelect: date => {
        this.onDateSelect(dateFromInput, dateFromPicker)
      }
    })
    this.pikadayInstances['date_from'] = dateFromPicker

    const dateToPicker = new Pikaday({
      field: dateToInput,
      minDate: dateFromPicker.getDate() || new Date(),
      onSelect: date => {
        this.onDateSelect(dateToInput, dateToPicker)
      }
    })
    this.pikadayInstances['date_to'] = dateToPicker
    if (this.showDateToPicker) {
      dateToPicker.show();
      this.showDateToPicker = false
    }
  }

  onDateSelect(input, picker) {
    if (input.name === 'date_from') {
      this.onDateFromSelect()
    }
    input.value = picker.toString();
    this.debouncedReloadResults({ o: 0 })
  }

  onDateFromSelect() {
    const dateFromPicker = this.pikadayInstances['date_from'],
          dateToPicker = this.pikadayInstances['date_to'],
          dateFromValue = dateFromPicker.toString(),
          dateToValue = dateToPicker.toString();

    if (dateFromValue) {
      const dateFrom = new Date(dateFromValue);

      if (dateToValue) {
        const dateTo = new Date(dateToValue);

        if (dateTo < dateFrom) {
          dateToPicker.setDate(dateFrom)
        }
      }
    }
    this.showDateToPicker = true
  }
}

$(document).ready(() => {
  $(".js-ProductsSearch-container").each((i, element) => {
    window.st.search = new ProductsSearchForm($(element))
  })
})




// WEBPACK FOOTER //
// ./app/webpack/frontend/initializers/jsProductsSearch.js